import React from "react";
import { Text, Flex, Card } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Sizes, Colors } from "@jsq/ds/styles";
import { type ScreeningMatchType } from "~/graphql/supergraphTypes";
import CollapsibleContent, {
  ChildrenCallbackObject,
} from "../../../../../Common/CollapsibleContent";
import { ProfileField } from "../../../ProfileField";
import { addressValue } from "../../../ProfileField/styles_vanilla.css";
import { ScreeningRecordEntityDetailsAdditionalInfo, IDsEntity } from "../../../types";
import { truncate } from "../../../utils";
import { LexisNexisMatchesTruncatedComments_Style } from "../styles_vanilla.css";
import {
  extractAdditionalInfoUrls,
  extractAKAs,
  extractName,
  getProfileInfo,
  parseAllAddressesFromLexisNexisMatch,
} from "../utils";
import {
  additionalInfoLink_style,
  alertText_style,
  matchDetailsId_style,
  typeInfo_style,
} from "./styles_vanilla.css";
export type ScreeningMatchDetailsProps = {
  lnMatch: ScreeningMatchType;
};

const ENTITY_TYPE_INDIVIDUAL = "Individual";

const getProfileField = (
  additionalInfo: ScreeningRecordEntityDetailsAdditionalInfo[] | null | undefined,
  fieldType: string,
): string => {
  return getProfileInfo(additionalInfo, "Type", fieldType)?.Value || "--";
};

const formatStringList = (items: string[]): React.ReactNode => {
  if (!items || !Array.isArray(items) || !items.length) {
    return <Text variant="regular-md">--</Text>;
  }
  return (
    <Flex flexDirection="column" gap={Sizes.XS}>
      {items.map((item, index) => {
        const lastOpenParen = item.lastIndexOf(" (");
        const lastChar = item.charAt(item.length - 1);
        if (lastOpenParen !== -1 && lastChar === ")") {
          const name = item.substring(0, lastOpenParen);
          const type = item.substring(lastOpenParen);
          return (
            <Text key={index} variant="regular-md">
              {name}
              <span className={typeInfo_style}>{type}</span>
            </Text>
          );
        }
        return (
          <Text key={index} variant="regular-md">
            {item}
          </Text>
        );
      })}
    </Flex>
  );
};

const formatIDsList = (items: IDsEntity[] | null | undefined): React.ReactNode => {
  if (!items || !Array.isArray(items) || !items.length) {
    return <Text variant="regular-md">--</Text>;
  }
  return (
    <Flex flexDirection="column" gap={Sizes.XS}>
      {items.map((id, index) => (
        <Text key={index} variant="regular-md">
          {id?.Number || ""}
          <span className={typeInfo_style}>{` (${id?.Type || ""})`}</span>
        </Text>
      ))}
    </Flex>
  );
};

const parseAlerts = (reasonListed?: string): string[] => {
  if (!reasonListed) return [];
  return reasonListed
    .split(",")
    .map((alert) => alert.trim())
    .filter(Boolean);
};

const CONTENT_COLLAPSE_THRESHOLD = 300;

const getAdditionalInfoTotalLength = (urls: string[]): number =>
  urls.reduce((total, url) => total + url.length + 3, 0);

const AdditionalInfoField = ({ urls }: { urls: string[] }) => {
  if (urls.length === 0) return null;
  return (
    <ProfileField
      label="Additional information"
      value={
        <div>
          {urls.map((url, index) => (
            <React.Fragment key={index}>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={additionalInfoLink_style}
              >
                {url}
              </a>
              {index < urls.length - 1 && " | "}
            </React.Fragment>
          ))}
        </div>
      }
    />
  );
};

export const ScreeningMatchDetails: React.FC<ScreeningMatchDetailsProps> = ({ lnMatch }) => {
  const entityDetails = lnMatch?.details?.EntityDetails;
  const isIndividual = entityDetails?.EntityType === ENTITY_TYPE_INDIVIDUAL;

  const akas = extractAKAs(entityDetails?.AKAs) || [];
  const ids = entityDetails?.IDs || [];
  const alerts = parseAlerts(lnMatch.details?.ReasonListed) || [];
  const comments = entityDetails?.Comments;
  const additionalInfoUrls = extractAdditionalInfoUrls(entityDetails?.AdditionalInfo);

  return (
    <Card variant="white">
      <Flex flexDirection="column" gap={Sizes.LG}>
        <Text variant="secondary-md">
          MATCH DETAILS{" "}
          <Text asSpan className={matchDetailsId_style}>
            ({lnMatch.id})
          </Text>
        </Text>

        <Flex flexDirection="column" gap={Sizes.MD}>
          <ProfileField
            label="Name"
            value={<Text variant="regular-md">{extractName(lnMatch)}</Text>}
          />

          <ProfileField
            label="Also known as (AKA)"
            value={formatStringList(
              akas?.map(
                (aka) => `${aka?.text || ""}${aka?.category ? ` (${aka.category})` : ""}`,
              ) || [],
            )}
          />

          <ProfileField
            label={isIndividual ? "Date of birth" : "Date of organization"}
            value={
              <Text variant="regular-md">
                {getProfileField(entityDetails?.AdditionalInfo, isIndividual ? "DOB" : "DOO")}
              </Text>
            }
          />

          <ProfileField
            label="Occupation"
            value={
              <Text variant="regular-md">
                {getProfileField(entityDetails?.AdditionalInfo, "Occupation")}
              </Text>
            }
          />

          {parseAllAddressesFromLexisNexisMatch(entityDetails?.Addresses).map((address) => (
            <ProfileField
              key={address.label}
              label={address.label}
              value={
                <Text variant="regular-md" className={addressValue}>
                  {address.value}
                </Text>
              }
            />
          ))}

          <ProfileField
            label="Place of birth / Nationality"
            value={
              <Text variant="regular-md">
                {getProfileField(entityDetails?.AdditionalInfo, "PlaceOfBirth")}
              </Text>
            }
          />

          <ProfileField label="IDs" value={formatIDsList(ids)} />

          <ProfileField
            label="Alert lists"
            value={
              alerts.length ? (
                <Flex alignItems="center" gap={Sizes.XS}>
                  <Icon type="exclamation-triangle" size="small" color={Colors.Warning600} />
                  <Text variant="regular-md" className={alertText_style}>
                    {alerts.join(", ")}
                  </Text>
                </Flex>
              ) : (
                <Text variant="regular-md">--</Text>
              )
            }
          />

          {(comments && comments.length > CONTENT_COLLAPSE_THRESHOLD) ||
          getAdditionalInfoTotalLength(additionalInfoUrls) > CONTENT_COLLAPSE_THRESHOLD ? (
            <CollapsibleContent showMoreText="View more" showLessText="View less">
              {({ isExpanded, button }: ChildrenCallbackObject) => (
                <Flex flexDirection="column" gap={Sizes.MD}>
                  <ProfileField
                    label="Comments / Other"
                    value={
                      comments ? (
                        <Text
                          variant="regular-md"
                          className={isExpanded ? "" : LexisNexisMatchesTruncatedComments_Style}
                        >
                          {isExpanded
                            ? comments
                            : truncate(comments, CONTENT_COLLAPSE_THRESHOLD)}
                        </Text>
                      ) : (
                        <Text variant="regular-md">--</Text>
                      )
                    }
                  />
                  {isExpanded && <AdditionalInfoField urls={additionalInfoUrls} />}
                  {button}
                </Flex>
              )}
            </CollapsibleContent>
          ) : (
            <>
              <ProfileField
                label="Comments / Other"
                value={
                  comments ? (
                    <Text variant="regular-md">{comments}</Text>
                  ) : (
                    <Text variant="regular-md">--</Text>
                  )
                }
              />
              <AdditionalInfoField urls={additionalInfoUrls} />
            </>
          )}
        </Flex>
      </Flex>
    </Card>
  );
};
